import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaPlay, FaArrowRight, FaCopy } from 'react-icons/fa';
import {
  trackSignupFree,
  trackStartFree,
  useYouTubeIframeAnalytics,
  withYouTubeAnalyticsParams
} from '../utils/analytics.js';
import { setPageSEO } from '../utils/seo.js';
import './WallOfLove.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerChildren = {
  visible: {
    transition: { staggerChildren: 0.1 }
  }
};

function WallOfLove() {
  const [copiedCode, setCopiedCode] = useState(false);
  const [expandedPreview, setExpandedPreview] = useState(null);
  const [showTutorialPlayer, setShowTutorialPlayer] = useState(false);
  const tutorialIframeRef = useYouTubeIframeAnalytics('Wall of Love Tutorial', showTutorialPlayer);

  useEffect(() => setPageSEO({
    title: 'Make Customer Love Visible - Clientalio',
    description: "Turn happy customer stories into visible social proof that builds trust and helps more visitors become customers.",
    canonical: 'https://clientalio.com/wall-of-love',
    ogTitle: 'Make Customer Love Visible - Clientalio',
    ogDescription: 'Put customer proof where buyers hesitate, compare, and decide whether to trust you.',
    twitterTitle: 'Make Customer Love Visible - Clientalio',
    twitterDescription: 'Turn happy customer stories into visible social proof that helps more visitors become customers.'
  }), []);

  const embedExamples = [
    {
      id: 'website',
      name: 'Website',
      description: 'Full-width Wall of Love at the bottom of your homepage',
      icon: '🌐',
      width: '100%',
      height: '800px'
    },
    {
      id: 'landing',
      name: 'Landing Page',
      description: 'Dedicated landing page showcasing customer love',
      icon: '📄',
      width: '1200px',
      height: '900px'
    },
    {
      id: 'portfolio',
      name: 'Portfolio',
      description: 'Showcase client testimonials in your portfolio',
      icon: '💼',
      width: '600px',
      height: '400px'
    },
    {
      id: 'saas',
      name: 'SaaS',
      description: 'Beautiful social proof for your SaaS product',
      icon: '🚀',
      width: '1200px',
      height: '700px'
    },
    {
      id: 'agency',
      name: 'Agency',
      description: 'Highlight your best client work and testimonials',
      icon: '🎯',
      width: '1200px',
      height: '600px'
    },
    {
      id: 'ecommerce',
      name: 'E-commerce',
      description: 'Drive conversions with customer testimonials',
      icon: '🛍️',
      width: '100%',
      height: '500px'
    }
  ];

const codeSnippet = `<script 
src="https://apiclientalio.azurewebsites.net/widgets/wall-of-love-embed.js"
data-id="c9f25234-ca2f-48bf-aa54-7228743bc067"
data-display-style="grid"
data-rotate="false"
data-title="Success in Their Words"
data-subtitle="See how customers are succeeding with us."
data-show-title="true"
data-show-subtitle="true"
data-theme="light"
data-max-view-cards="2"
data-interval="3000"
data-enable-animation="true"
data-animation-icon="heart"
data-animation-direction="down-top"
data-design="Classic_default">
</script>`;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(codeSnippet);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <div className="wall-of-love-page">
      {/* Hero */}
      <motion.section 
        className="wol-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <motion.div
            className="wol-hero-panel"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <div className="wol-hero-copy">
              <span className="wol-hero-eyebrow">Wall of Love</span>
              <h1>Make Customer Love Visible Before Buyers Leave</h1>
              <p>Turn real customer stories into trust signals that help visitors believe you, remember you, and choose you.</p>
              <div className="wol-hero-proof" aria-label="Product benefits">
                <span>No credit card</span>
                <span>Live in minutes</span>
                <span>Fully customizable</span>
              </div>
            </div>
            <div className="wol-hero-action-card">
              <strong>Build your Wall of Love</strong>
              <p>Collect, curate, and publish your best customer stories from one place.</p>
              <button
                className="btn-primary"
                onClick={() => { trackSignupFree('wall_of_love_hero'); window.location.href = '/signup'; }}
              >
                Start free
                <FaArrowRight aria-hidden="true" />
              </button>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Embed Anywhere */}
      <motion.section 
        className="embed-anywhere"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Put Trust Where Buyers Decide</h2>
            <p>Add customer proof to the pages where people compare, hesitate, and need confidence.</p>
          </motion.div>

          <motion.div 
            className="embed-examples"
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {embedExamples.map((example) => (
              <motion.div 
                key={example.id} 
                className="embed-card"
                variants={fadeInUp}
                whileHover={{ y: -8 }}
                onClick={() => setExpandedPreview(expandedPreview === example.id ? null : example.id)}
              >
                <div className="embed-icon">{example.icon}</div>
                <h3>{example.name}</h3>
                <p>{example.description}</p>
                {/* <button className="embed-btn">Learn More</button> */}
              </motion.div>
            ))}
          </motion.div>
            <br />
              <br />
          {/* Preview Expanded */}
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Copy & Embed</h2>
            <p>Simple code snippet to add your Wall of Love anywhere</p>
          </motion.div>

          <motion.div 
            className="code-box"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="code-header">
              <span className="code-lang">HTML</span>
              <button 
                className={`copy-btn ${copiedCode ? 'copied' : ''}`}
                onClick={handleCopyCode}
              >
                <FaCopy /> {copiedCode ? 'Copied!' : 'Copy Code'}
              </button>
            </div>
            <pre className="code-content">{codeSnippet}</pre>
          </motion.div>

          <motion.div 
            className="customization-info"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="info-card">
              <h3>🎨 Full Customization</h3>
              <p>Customize colors, fonts, layouts, and sizes directly from your Clientalio dashboard without touching code.</p>
            </div>
            <div className="info-card">
              <h3>⚡ Lightning Fast</h3>
              <p>Optimized performance with lazy loading and CDN delivery for fastest load times.</p>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* YouTube Tutorial */}
      <motion.section 
        className="tutorial-section"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Wall of Love Tutorial</h2>
            <p>Watch how easy it is to create and customize your Wall of Love</p>
          </motion.div>

          <motion.div 
            className="video-embed-wrapper"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <div className="video-embed custom-video-player">
              {!showTutorialPlayer ? (
                <div
                  className="tutorial-video-poster"
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowTutorialPlayer(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      setShowTutorialPlayer(true);
                    }
                  }}
                  aria-label="Play Wall of Love tutorial"
                >
                  <img
                    src="https://img.youtube.com/vi/pcVPcqUJiNo/maxresdefault.jpg"
                    alt="Wall of Love Tutorial"
                  />
                  <div className="tutorial-video-overlay">
                    <button className="tutorial-play-button" type="button" aria-hidden="true">
                      <FaPlay />
                    </button>
                    <div className="tutorial-video-meta">
                      <span>Clientalio Tutorial</span>
                      <strong>Wall of Love Tutorial</strong>
                    </div>
                  </div>
                </div>
              ) : (
                <iframe
                  ref={tutorialIframeRef}
                  src={withYouTubeAnalyticsParams('https://www.youtube.com/embed/pcVPcqUJiNo?autoplay=1&rel=0&modestbranding=1')}
                  title="Wall of Love Tutorial"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              )}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section 
        className="wol-cta"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <motion.div
            className="wol-cta-inner"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2>Ready to Stop Hiding Your Best Customer Stories?</h2>
            <p>Create your branded trust page, share your first request, and make customer love visible in under 2 minutes.</p>
            <div className="cta-actions">
              <button
                className="btn-primary"
                onClick={() => { trackStartFree('wall_of_love_cta'); window.location.href = '/signup'; }}
              >
                Start FREE In Under 2 Minutes
                <FaArrowRight style={{ marginLeft: '10px' }} />
              </button>
              {/* <button className="btn-secondary">Schedule Demo</button> */}
            </div>
            <p className="cta-trust">✓ Branded page  ✓ First customer request  ✓ No credit card required</p>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}

export default WallOfLove;
